import { useCallback, useEffect, useMemo, useState } from 'react';

import AcademiaDateField from '../common/AcademiaDateField';
import AcademiaSelect from '../common/AcademiaSelect';
import AcademiaTimeField from '../common/AcademiaTimeField';
import { AcademicPanelCard } from '../admin/academia/AcademicPanelCard';
import AdminPagination from '../admin/AdminPagination';
import AdminToast from '../admin/AdminToast';
import ElearningExerciseEditor, { jsonToExerciseQuestions } from '../elearning/ElearningExerciseEditor';
import LucideSymbolIcon from '../icons/LucideSymbolIcon';
import FormateurPageShell from './FormateurPageShell';
import {
  EVALUATION_CORRECTION_MODES,
  EVALUATION_SESSION_STATUT_LABELS,
  EVALUATION_SESSION_TYPES,
  TENTATIVE_STATUT_LABELS,
  evaluationIsLiveType,
  evaluationRequiresQuestions,
  type EvaluationCorrectionMode,
  type EvaluationSessionType,
} from '../../constants/evaluationSession';
import { useEvaluationCountdown } from '../../hooks/useEvaluationCountdown';
import {
  closeFormateurEvaluation,
  createFormateurEvaluation,
  fetchFormateurEvaluationCohortes,
  fetchFormateurEvaluationDetail,
  fetchFormateurEvaluationLiveStatus,
  fetchFormateurEvaluationTentatives,
  fetchFormateurEvaluations,
  fetchFormateurTentativeDetail,
  gradeFormateurTentative,
  launchLiveFormateurEvaluation,
  publishFormateurEvaluation,
} from '../../services/academiaFormateurService';

type ToastState = { message: string; variant: 'success' | 'error' } | null;

const EVALUATIONS_PAGE_SIZE = 5;
const TENTATIVES_PAGE_SIZE = 8;

function paginationSummary(total: number, page: number, pageSize: number, emptyLabel: string) {
  if (total === 0) return emptyLabel;
  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);
  return `Affichage ${start}–${end} sur ${total}`;
}

const EMPTY_FORM = {
  idCohorte: '',
  titre: '',
  typeEvaluation: 'QUESTIONNAIRE' as EvaluationSessionType,
  modeCorrection: 'AUTOMATIQUE' as EvaluationCorrectionMode,
  dureeMinutes: '60',
  coefficient: '1',
  instructions: '',
  dateOuverture: '',
  dateFermeture: '',
};

type EvaluationRow = Awaited<ReturnType<typeof fetchFormateurEvaluations>>[number];

function splitDateTimeLocal(value: string) {
  if (!value || value.length < 16) {
    const now = new Date();
    return { date: now.toISOString().slice(0, 10), time: '09:00' };
  }
  return { date: value.slice(0, 10), time: value.slice(11, 16) };
}

function joinDateTimeLocal(date: string, time: string) {
  return `${date}T${time}`;
}

function evaluationStatusBadgeClass(statut: string) {
  switch (statut) {
    case 'BROUILLON':
      return 'formateur-evaluation-card__status--draft';
    case 'PUBLIEE':
      return 'formateur-evaluation-card__status--published';
    case 'OUVERTE':
      return 'formateur-evaluation-card__status--open';
    case 'TERMINEE':
      return 'formateur-evaluation-card__status--done';
    case 'ANNULEE':
      return 'formateur-evaluation-card__status--cancelled';
    default:
      return 'formateur-evaluation-card__status--done';
  }
}

function splitCohorteLabel(label: string | null | undefined) {
  if (!label) return { code: '—', libelle: '' };
  const separator = ' — ';
  const idx = label.indexOf(separator);
  if (idx === -1) return { code: label, libelle: '' };
  return { code: label.slice(0, idx), libelle: label.slice(idx + separator.length) };
}

function EvaluationPickerCard({
  row,
  selected,
  onSelect,
}: {
  row: EvaluationRow;
  selected: boolean;
  onSelect: () => void;
}) {
  const cohorte = splitCohorteLabel(row.cohorteLabel);
  const statutLabel = EVALUATION_SESSION_STATUT_LABELS[row.statut] ?? row.statut;
  const isDraft = row.statut === 'BROUILLON';

  return (
    <button
      type="button"
      onClick={onSelect}
      className={[
        'formateur-evaluation-card',
        selected ? 'formateur-evaluation-card--active' : '',
        isDraft ? 'formateur-evaluation-card--draft' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      aria-pressed={selected}
    >
      <span className="formateur-evaluation-card__accent" aria-hidden />
      <span className="formateur-evaluation-card__inner">
        <span className="formateur-evaluation-card__head">
          <span className="formateur-evaluation-card__icon">
            <LucideSymbolIcon name="quiz" tone="inherit" size={18} />
          </span>
          <span
            className={`formateur-evaluation-card__status ${evaluationStatusBadgeClass(row.statut)}`}
            title={statutLabel}
          >
            {statutLabel}
          </span>
        </span>
        <span className="formateur-evaluation-card__title" title={row.titre}>
          {row.titre}
        </span>
        {cohorte.libelle ? (
          <span className="formateur-evaluation-card__cohorte" title={row.cohorteLabel ?? undefined}>
            {cohorte.code} · {cohorte.libelle}
          </span>
        ) : (
          <span className="formateur-evaluation-card__cohorte">{cohorte.code}</span>
        )}
        <span className="formateur-evaluation-card__chips">
          <span className="formateur-evaluation-card__chip" title={row.typeLabel}>
            {row.typeLabel}
          </span>
          <span className="formateur-evaluation-card__chip formateur-evaluation-card__chip--muted">
            {row.dureeMinutes} min
          </span>
          {row.nombreTentatives > 0 ? (
            <span className="formateur-evaluation-card__chip formateur-evaluation-card__chip--muted">
              {row.nombreTentatives} copie{row.nombreTentatives > 1 ? 's' : ''}
            </span>
          ) : null}
        </span>
        {selected ? (
          <span className="formateur-evaluation-card__active">
            <LucideSymbolIcon name="check_circle" tone="inherit" size={14} />
            Sélectionnée
          </span>
        ) : null}
      </span>
    </button>
  );
}

export default function FormateurEvaluationsPanel() {
  const [rows, setRows] = useState<Awaited<ReturnType<typeof fetchFormateurEvaluations>>>([]);
  const [cohortes, setCohortes] = useState<Awaited<ReturnType<typeof fetchFormateurEvaluationCohortes>>>([]);
  const [selectedId, setSelectedId] = useState('');
  const [tentatives, setTentatives] = useState<Awaited<ReturnType<typeof fetchFormateurEvaluationTentatives>>>([]);
  const [selectedTentativeId, setSelectedTentativeId] = useState('');
  const [tentativeDetail, setTentativeDetail] = useState<Awaited<
    ReturnType<typeof fetchFormateurTentativeDetail>
  > | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_FORM);
  const [questions, setQuestions] = useState(jsonToExerciseQuestions({ questions: [] }));
  const [gradeForm, setGradeForm] = useState({ noteSur20: '', commentaireFormateur: '' });
  const [liveStatus, setLiveStatus] = useState<Awaited<
    ReturnType<typeof fetchFormateurEvaluationLiveStatus>
  > | null>(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<ToastState>(null);
  const [evaluationsPage, setEvaluationsPage] = useState(1);
  const [tentativesPage, setTentativesPage] = useState(1);

  const { label: liveCountdownLabel } = useEvaluationCountdown(liveStatus?.sessionRemainingMs);

  const selected = useMemo(() => rows.find((r) => r.id === selectedId) ?? null, [rows, selectedId]);

  const evaluationsTotal = rows.length;
  const evaluationsTotalPages = Math.max(1, Math.ceil(evaluationsTotal / EVALUATIONS_PAGE_SIZE));
  const paginatedEvaluations = useMemo(() => {
    const start = (evaluationsPage - 1) * EVALUATIONS_PAGE_SIZE;
    return rows.slice(start, start + EVALUATIONS_PAGE_SIZE);
  }, [rows, evaluationsPage]);
  const evaluationsPaginationSummary = useMemo(
    () => paginationSummary(evaluationsTotal, evaluationsPage, EVALUATIONS_PAGE_SIZE, 'Aucune épreuve'),
    [evaluationsTotal, evaluationsPage],
  );

  const tentativesTotal = tentatives.length;
  const tentativesTotalPages = Math.max(1, Math.ceil(tentativesTotal / TENTATIVES_PAGE_SIZE));
  const paginatedTentatives = useMemo(() => {
    const start = (tentativesPage - 1) * TENTATIVES_PAGE_SIZE;
    return tentatives.slice(start, start + TENTATIVES_PAGE_SIZE);
  }, [tentatives, tentativesPage]);
  const tentativesPaginationSummary = useMemo(
    () => paginationSummary(tentativesTotal, tentativesPage, TENTATIVES_PAGE_SIZE, 'Aucune copie'),
    [tentativesTotal, tentativesPage],
  );

  useEffect(() => {
    setEvaluationsPage(1);
  }, [rows.length]);

  useEffect(() => {
    if (evaluationsPage > evaluationsTotalPages) {
      setEvaluationsPage(evaluationsTotalPages);
    }
  }, [evaluationsPage, evaluationsTotalPages]);

  useEffect(() => {
    setTentativesPage(1);
  }, [selectedId]);

  useEffect(() => {
    if (tentativesPage > tentativesTotalPages) {
      setTentativesPage(tentativesTotalPages);
    }
  }, [tentativesPage, tentativesTotalPages]);

  const showToast = useCallback((message: string, variant: 'success' | 'error') => {
    setToast({ message, variant });
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const [list, cohorteList] = await Promise.all([
        fetchFormateurEvaluations(),
        fetchFormateurEvaluationCohortes(),
      ]);
      setRows(list);
      setCohortes(cohorteList);
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Chargement impossible', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  const loadTentatives = useCallback(
    async (evaluationId: string) => {
      try {
        setTentatives(await fetchFormateurEvaluationTentatives(evaluationId));
      } catch {
        setTentatives([]);
      }
    },
    [],
  );

  useEffect(() => {
    void loadList();
  }, [loadList]);

  const loadLiveStatus = useCallback(async (evaluationId: string) => {
    try {
      setLiveStatus(await fetchFormateurEvaluationLiveStatus(evaluationId));
    } catch {
      setLiveStatus(null);
    }
  }, []);

  useEffect(() => {
    if (!selectedId) {
      setTentatives([]);
      setLiveStatus(null);
      return;
    }
    void loadTentatives(selectedId);
    void loadLiveStatus(selectedId);
  }, [selectedId, loadTentatives, loadLiveStatus]);

  useEffect(() => {
    if (!selectedId || !selected || selected.statut !== 'OUVERTE') return undefined;
    const timer = window.setInterval(() => {
      void loadLiveStatus(selectedId);
      void loadTentatives(selectedId);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [selectedId, selected?.statut, loadLiveStatus, loadTentatives, selected]);

  useEffect(() => {
    if (!selectedTentativeId) {
      setTentativeDetail(null);
      return;
    }
    void (async () => {
      try {
        const detail = await fetchFormateurTentativeDetail(selectedTentativeId);
        setTentativeDetail(detail);
        setGradeForm({
          noteSur20: detail.noteSur20 != null ? String(detail.noteSur20) : '',
          commentaireFormateur: detail.commentaireFormateur ?? '',
        });
      } catch (e) {
        showToast(e instanceof Error ? e.message : 'Copie introuvable', 'error');
      }
    })();
  }, [selectedTentativeId, showToast]);

  const runAction = async (action: () => Promise<{ message?: string } | void>, fallback: string) => {
    try {
      const result = await action();
      const message =
        typeof result === 'object' && result !== null && result.message
          ? result.message
          : 'Action réussie';
      showToast(message, 'success');
      await loadList();
      if (selectedId) await loadTentatives(selectedId);
    } catch (e) {
      showToast(e instanceof Error ? e.message : fallback, 'error');
    }
  };

  const handleCreate = async () => {
    if (!createForm.idCohorte || !createForm.titre.trim()) {
      showToast('Cohorte et titre requis', 'error');
      return;
    }
    const type = createForm.typeEvaluation;
    const payload: Record<string, unknown> = {
      idCohorte: createForm.idCohorte,
      titre: createForm.titre.trim(),
      typeEvaluation: type,
      modeCorrection: evaluationRequiresQuestions(type) ? createForm.modeCorrection : 'MANUELLE',
      dureeMinutes: Number(createForm.dureeMinutes) || 60,
      coefficient: Number(createForm.coefficient) || 1,
      instructions: createForm.instructions.trim() || undefined,
    };
    if (!evaluationIsLiveType(type)) {
      if (!createForm.dateOuverture || !createForm.dateFermeture) {
        showToast('Dates d’ouverture et de fermeture requises', 'error');
        return;
      }
      payload.dateOuverture = new Date(createForm.dateOuverture).toISOString();
      payload.dateFermeture = new Date(createForm.dateFermeture).toISOString();
    }
    if (evaluationRequiresQuestions(type)) {
      payload.contenuJson = { questions };
    }
    try {
      const created = await createFormateurEvaluation(payload);
      showToast('Évaluation créée en brouillon', 'success');
      setShowCreate(false);
      setCreateForm(EMPTY_FORM);
      setQuestions(jsonToExerciseQuestions({ questions: [] }));
      await loadList();
      if (created?.id) setSelectedId(created.id);
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Création impossible', 'error');
    }
  };

  const openCount = rows.filter((r) => r.statut === 'OUVERTE').length;
  const draftCount = rows.filter((r) => r.statut === 'BROUILLON').length;

  return (
    <FormateurPageShell
      title="Évaluations"
      description="Créez des épreuves typées, notifiez les apprenants et corrigez les copies dans cet espace dédié."
      sideCard={{
        label: 'Épreuves',
        value: loading ? '…' : String(rows.length),
        helper:
          rows.length > 0
            ? `${openCount} ouverte${openCount > 1 ? 's' : ''} · ${draftCount} brouillon${draftCount > 1 ? 's' : ''}`
            : 'Examens, TP, questionnaires, mock…',
        sideLinks: [{ label: 'Mes sessions', to: '/formateur/sessions', icon: 'event' }],
      }}
      isLoading={loading && rows.length === 0}
      onRetry={() => void loadList()}
      loadingMessage="Chargement des évaluations…"
    >
      <AdminToast message={toast?.message ?? null} variant={toast?.variant} onClose={() => setToast(null)} />

      <div className="formateur-portal">
        <div className="mb-4 flex flex-wrap justify-end gap-2">
          <button
            type="button"
            className="formateur-sessions-btn formateur-sessions-btn--primary"
            onClick={() => {
              setShowCreate((v) => !v);
              setToast(null);
            }}
          >
            {showCreate ? 'Fermer le formulaire' : '+ Nouvelle évaluation'}
          </button>
        </div>

        {showCreate ? (
          <div className="mb-6">
            <AcademicPanelCard
              icon="add_circle"
              title="Nouvelle évaluation"
              subtitle="Les épreuves sur place se lancent en direct ; les épreuves à domicile s’ouvrent sur une plage horaire avec chronomètre individuel."
              variant="accent"
            >
              <div className="formateur-notes-form">
                <div className="formateur-notes-form__section formateur-notes-form__grid">
                  <label className="formateur-notes-field">
                    <span className="formateur-notes-field__label">Cohorte</span>
                    <AcademiaSelect
                      className="w-full"
                      value={createForm.idCohorte}
                      onChange={(e) => setCreateForm((f) => ({ ...f, idCohorte: e.target.value }))}
                    >
                      <option value="">Choisir…</option>
                      {cohortes.map((c) => (
                        <option key={c.idCohorte} value={c.idCohorte}>
                          {c.cohorteLabel}
                        </option>
                      ))}
                    </AcademiaSelect>
                  </label>
                  <label className="formateur-notes-field">
                    <span className="formateur-notes-field__label">Type d’épreuve</span>
                    <AcademiaSelect
                      className="w-full"
                      value={createForm.typeEvaluation}
                      onChange={(e) => {
                        const typeEvaluation = e.target.value as EvaluationSessionType;
                        setCreateForm((f) => ({
                          ...f,
                          typeEvaluation,
                          modeCorrection: evaluationRequiresQuestions(typeEvaluation)
                            ? f.modeCorrection
                            : 'MANUELLE',
                        }));
                      }}
                    >
                      {EVALUATION_SESSION_TYPES.map((t) => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </AcademiaSelect>
                  </label>
                  {evaluationRequiresQuestions(createForm.typeEvaluation) ? (
                    <div className="formateur-notes-field formateur-notes-field--full">
                      <span className="formateur-notes-field__label">Mode de correction</span>
                      <AcademiaSelect
                        className="w-full"
                        value={createForm.modeCorrection}
                        onChange={(e) =>
                          setCreateForm((f) => ({
                            ...f,
                            modeCorrection: e.target.value as EvaluationCorrectionMode,
                          }))
                        }
                      >
                        {EVALUATION_CORRECTION_MODES.map((m) => (
                          <option key={m.value} value={m.value}>
                            {m.label}
                          </option>
                        ))}
                      </AcademiaSelect>
                      <p className="formateur-notes-form__hint">
                        {
                          EVALUATION_CORRECTION_MODES.find((m) => m.value === createForm.modeCorrection)
                            ?.description
                        }
                      </p>
                    </div>
                  ) : (
                    <div className="formateur-notes-field formateur-notes-field--full">
                      <span className="formateur-notes-field__label">Mode de correction</span>
                      <p className="text-sm text-on-surface-variant">
                        Correction manuelle — cette épreuve repose sur un dépôt de fichier, sans questionnaire
                        noté automatiquement.
                      </p>
                    </div>
                  )}
                  <label className="formateur-notes-field">
                    <span className="formateur-notes-field__label">Titre</span>
                    <input
                      className="academia-field w-full"
                      placeholder="Ex. Examen de mi-parcours"
                      value={createForm.titre}
                      onChange={(e) => setCreateForm((f) => ({ ...f, titre: e.target.value }))}
                    />
                  </label>
                  <label className="formateur-notes-field">
                    <span className="formateur-notes-field__label">Durée (minutes)</span>
                    <input
                      type="number"
                      min={1}
                      className="academia-field w-full"
                      value={createForm.dureeMinutes}
                      onChange={(e) => setCreateForm((f) => ({ ...f, dureeMinutes: e.target.value }))}
                    />
                  </label>
                  <label className="formateur-notes-field">
                    <span className="formateur-notes-field__label">Coefficient</span>
                    <input
                      type="number"
                      min={0}
                      step={0.5}
                      className="academia-field w-full"
                      value={createForm.coefficient}
                      onChange={(e) => setCreateForm((f) => ({ ...f, coefficient: e.target.value }))}
                    />
                  </label>
                  {!evaluationIsLiveType(createForm.typeEvaluation) ? (
                    <>
                      <div className="formateur-notes-field">
                        <span className="formateur-notes-field__label">Ouverture</span>
                        <div className="formateur-notes-datetime">
                          <AcademiaDateField
                            hideLabel
                            className="max-w-none w-full"
                            value={splitDateTimeLocal(createForm.dateOuverture).date}
                            onChange={(date) => {
                              const { time } = splitDateTimeLocal(createForm.dateOuverture);
                              setCreateForm((f) => ({
                                ...f,
                                dateOuverture: joinDateTimeLocal(date, time),
                              }));
                            }}
                          />
                          <AcademiaTimeField
                            hideLabel
                            className="max-w-none w-full"
                            value={splitDateTimeLocal(createForm.dateOuverture).time}
                            onChange={(time) => {
                              const { date } = splitDateTimeLocal(createForm.dateOuverture);
                              setCreateForm((f) => ({
                                ...f,
                                dateOuverture: joinDateTimeLocal(date, time),
                              }));
                            }}
                          />
                        </div>
                      </div>
                      <div className="formateur-notes-field">
                        <span className="formateur-notes-field__label">Fermeture</span>
                        <div className="formateur-notes-datetime">
                          <AcademiaDateField
                            hideLabel
                            className="max-w-none w-full"
                            value={splitDateTimeLocal(createForm.dateFermeture).date}
                            onChange={(date) => {
                              const { time } = splitDateTimeLocal(createForm.dateFermeture);
                              setCreateForm((f) => ({
                                ...f,
                                dateFermeture: joinDateTimeLocal(date, time),
                              }));
                            }}
                          />
                          <AcademiaTimeField
                            hideLabel
                            className="max-w-none w-full"
                            value={splitDateTimeLocal(createForm.dateFermeture).time}
                            onChange={(time) => {
                              const { date } = splitDateTimeLocal(createForm.dateFermeture);
                              setCreateForm((f) => ({
                                ...f,
                                dateFermeture: joinDateTimeLocal(date, time),
                              }));
                            }}
                          />
                        </div>
                      </div>
                    </>
                  ) : null}
                  <label className="formateur-notes-field formateur-notes-field--full">
                    <span className="formateur-notes-field__label">Consignes</span>
                    <textarea
                      className="academia-field w-full"
                      rows={3}
                      placeholder="Instructions pour les apprenants…"
                      value={createForm.instructions}
                      onChange={(e) => setCreateForm((f) => ({ ...f, instructions: e.target.value }))}
                    />
                  </label>
                </div>
                {evaluationRequiresQuestions(createForm.typeEvaluation) ? (
                  <div className="formateur-notes-form__section">
                    <h4 className="formateur-notes-form__heading">
                      <LucideSymbolIcon name="quiz" tone="inherit" size={18} />
                      Banque de questions
                      {createForm.modeCorrection === 'AUTOMATIQUE' ? ' — bonnes réponses' : ''}
                    </h4>
                    {createForm.modeCorrection === 'AUTOMATIQUE' ? (
                      <p className="formateur-notes-form__hint mb-3">
                        Renseignez l’énoncé et la bonne réponse pour chaque question : le système comparera les
                        réponses des apprenants et attribuera la note à la soumission.
                      </p>
                    ) : (
                      <p className="formateur-notes-form__hint mb-3">
                        Les questions servent à recueillir les réponses ; vous corrigerez chaque copie manuellement
                        après soumission.
                      </p>
                    )}
                    <ElearningExerciseEditor value={questions} onChange={setQuestions} />
                  </div>
                ) : null}
                <div>
                  <button
                    type="button"
                    className="formateur-sessions-btn formateur-sessions-btn--primary"
                    onClick={() => void handleCreate()}
                  >
                    Enregistrer le brouillon
                  </button>
                </div>
              </div>
            </AcademicPanelCard>
          </div>
        ) : null}

        <div className="grid gap-6 xl:grid-cols-[1fr_1.15fr]">
          <section className="formateur-sessions-list" aria-labelledby="formateur-evaluations-list-heading">
            <header className="formateur-sessions-list__header">
              <span className="formateur-sessions-list__header-icon" aria-hidden>
                <LucideSymbolIcon name="quiz" tone="inherit" size={22} />
              </span>
              <div>
                <h2 id="formateur-evaluations-list-heading" className="formateur-sessions-list__title">
                  Mes épreuves
                </h2>
                <p className="formateur-sessions-list__subtitle">
                  Cliquez sur une carte pour publier, lancer en direct ou corriger les copies.
                </p>
              </div>
            </header>
            <div className="formateur-sessions-list__body">
              {rows.length === 0 ? (
                <p className="formateur-sessions-empty">Aucune évaluation pour le moment.</p>
              ) : (
                <>
                  <div className="formateur-evaluations-list__grid">
                    {paginatedEvaluations.map((row) => (
                      <EvaluationPickerCard
                        key={row.id}
                        row={row}
                        selected={selectedId === row.id}
                        onSelect={() => {
                          setSelectedId(row.id);
                          setSelectedTentativeId('');
                        }}
                      />
                    ))}
                  </div>
                  <AdminPagination
                    className="formateur-presence-pagination mt-4"
                    currentPage={evaluationsPage}
                    totalPages={evaluationsTotalPages}
                    onPageChange={setEvaluationsPage}
                    summary={evaluationsPaginationSummary}
                  />
                </>
              )}
            </div>
          </section>

          <div className="space-y-4">
            {selected ? (
              <>
                <section className="formateur-sessions-workspace" aria-label="Détail de l’épreuve">
                  <header className="formateur-sessions-workspace__head">
                    <div className="min-w-0">
                      <p className="formateur-sessions-workspace__eyebrow">Épreuve sélectionnée</p>
                      <h3 className="formateur-sessions-workspace__title">{selected.titre}</h3>
                      <p className="formateur-sessions-workspace__chips">
                        <span className="formateur-sessions-workspace__chip">{selected.typeLabel}</span>
                        <span className="formateur-sessions-workspace__chip">{selected.cohorteLabel}</span>
                        <span className="formateur-sessions-workspace__chip">{selected.dureeMinutes} min</span>
                        <span className="formateur-sessions-workspace__chip">
                          {(selected as { modeCorrectionLabel?: string }).modeCorrectionLabel ??
                            (selected.modeCorrection === 'MANUELLE'
                              ? 'Correction manuelle'
                              : 'Correction automatique')}
                        </span>
                      </p>
                    </div>
                  </header>
                  <div className="formateur-sessions-panel">
                    {selected.instructions ? (
                      <p className="mb-4 whitespace-pre-wrap text-sm leading-relaxed text-on-surface-variant">
                        {selected.instructions}
                      </p>
                    ) : null}
                    <div className="flex flex-wrap gap-2">
                      {selected.statut === 'BROUILLON' ? (
                        <button
                          type="button"
                          className="academic-panel-btn academic-panel-btn--primary"
                          onClick={() =>
                            void runAction(() => publishFormateurEvaluation(selected.id), 'Publication impossible')
                          }
                        >
                          Publier et notifier
                        </button>
                      ) : null}
                      {selected.mode === 'SYNCHRONE' &&
                      (selected.statut === 'PUBLIEE' || selected.statut === 'OUVERTE') ? (
                        <button
                          type="button"
                          className="academic-panel-btn academic-panel-btn--primary"
                          onClick={() =>
                            void runAction(() => launchLiveFormateurEvaluation(selected.id), 'Lancement impossible')
                          }
                        >
                          <LucideSymbolIcon name="play_circle" tone="inherit" size={16} />
                          Lancer en direct
                        </button>
                      ) : null}
                      {selected.statut === 'OUVERTE' || selected.statut === 'PUBLIEE' ? (
                        <button
                          type="button"
                          className="academic-panel-btn academic-panel-btn--secondary"
                          onClick={() =>
                            void runAction(() => closeFormateurEvaluation(selected.id), 'Clôture impossible')
                          }
                        >
                          Clôturer
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className="academic-panel-btn academic-panel-btn--ghost"
                        onClick={() =>
                          void fetchFormateurEvaluationDetail(selected.id).then(() => loadTentatives(selected.id))
                        }
                      >
                        Actualiser
                      </button>
                    </div>
                  </div>
                </section>

                {selected.statut === 'OUVERTE' && liveStatus ? (
                  <AcademicPanelCard
                    icon="timer"
                    title="Suivi en direct"
                    subtitle={`${liveStatus.stats.enCours} en cours · ${liveStatus.stats.soumis} soumis · ${liveStatus.stats.enAttente} en attente · ${liveStatus.stats.expire} expiré`}
                    variant="preview"
                    badge={
                      liveStatus.sessionRemainingMs != null ? (
                        <span className="inline-flex items-center gap-2 rounded-lg border border-primary/20 bg-white px-3 py-1.5 font-mono text-base font-bold text-primary">
                          <LucideSymbolIcon name="timer" tone="inherit" size={18} />
                          {liveCountdownLabel}
                        </span>
                      ) : null
                    }
                  >
                    <ul className="formateur-notes-picker max-h-44">
                      {liveStatus.participants.map((p) => (
                        <li key={p.id}>
                          <div className="formateur-notes-picker__item pointer-events-none">
                            <span className="formateur-notes-picker__name">{p.apprenantLabel}</span>
                            <span className="formateur-notes-picker__mat">
                              {TENTATIVE_STATUT_LABELS[p.statut] ?? p.statut}
                              {p.remainingMs != null && p.statut === 'EN_COURS'
                                ? ` · ${Math.ceil(p.remainingMs / 60000)} min`
                                : ''}
                            </span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </AcademicPanelCard>
                ) : null}

                <AcademicPanelCard
                  icon="assignment"
                  title={`Copies des apprenants (${tentatives.length})`}
                  subtitle="Seul vous voyez les travaux soumis pour vos épreuves."
                >
                  {tentatives.length === 0 ? (
                    <p className="formateur-sessions-empty">Aucune copie pour le moment.</p>
                  ) : (
                    <ul className="formateur-notes-picker max-h-72">
                      {paginatedTentatives.map((t) => (
                        <li key={t.id}>
                          <button
                            type="button"
                            className={`formateur-notes-picker__item${
                              selectedTentativeId === t.id ? ' formateur-notes-picker__item--active' : ''
                            }`}
                            onClick={() => setSelectedTentativeId(t.id)}
                          >
                            <span className="formateur-notes-picker__name">{t.apprenantLabel}</span>
                            <span className="formateur-notes-picker__mat">
                              {TENTATIVE_STATUT_LABELS[t.statut] ?? t.statut}
                              {t.noteSur20 != null ? ` · ${t.noteSur20}/20` : ''}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  <AdminPagination
                    className="formateur-presence-pagination mt-4"
                    currentPage={tentativesPage}
                    totalPages={tentativesTotalPages}
                    onPageChange={setTentativesPage}
                    summary={tentativesPaginationSummary}
                  />
                </AcademicPanelCard>

                {tentativeDetail ? (
                  <AcademicPanelCard
                    icon="edit_note"
                    title={`Correction — ${tentativeDetail.apprenantLabel}`}
                    subtitle={
                      selected.modeCorrection === 'AUTOMATIQUE'
                        ? 'Note calculée à la soumission selon vos bonnes réponses — modifiable ci-dessous.'
                        : 'Saisissez la note manuellement ; elle sera reportée sur le relevé de l’apprenant.'
                    }
                    variant="accent"
                  >
                    {selected.modeCorrection === 'AUTOMATIQUE' && tentativeDetail.noteSur20 != null ? (
                      <p className="academic-panel-stat-banner mb-4">
                        Note automatique : <strong>{tentativeDetail.noteSur20}/20</strong>
                        {tentativeDetail.scoreAuto != null
                          ? ` (${tentativeDetail.scoreAuto} % de bonnes réponses)`
                          : ''}
                      </p>
                    ) : null}
                    {tentativeDetail.urlFichier ? (
                      <p className="mb-3 text-sm">
                        Fichier :{' '}
                        <a
                          href={tentativeDetail.urlFichier}
                          className="font-semibold text-primary underline"
                          target="_blank"
                          rel="noreferrer"
                        >
                          Télécharger le dépôt
                        </a>
                      </p>
                    ) : null}
                    {tentativeDetail.reponsesDetail && tentativeDetail.reponsesDetail.length > 0 ? (
                      <ul className="mb-4 space-y-2 rounded-lg border border-outline-variant/25 bg-surface-container-lowest p-3 text-sm">
                        {tentativeDetail.reponsesDetail.map((r) => (
                          <li key={r.id}>
                            <p className="font-medium">{r.libelle || r.id}</p>
                            <p className="text-on-surface-variant">
                              {r.reponse == null || r.reponse === ''
                                ? '—'
                                : Array.isArray(r.reponse)
                                  ? r.reponse.join(' → ')
                                  : String(r.reponse)}
                            </p>
                          </li>
                        ))}
                      </ul>
                    ) : tentativeDetail.hasReponses ? (
                      <p className="mb-3 text-sm text-on-surface-variant">Réponses enregistrées.</p>
                    ) : null}
                    {tentativeDetail.scoreAuto != null ? (
                      <p className="mb-3 text-sm font-medium">Score auto : {tentativeDetail.scoreAuto} %</p>
                    ) : null}
                    <div className="formateur-notes-form__grid">
                      <label className="formateur-notes-field">
                        <span className="formateur-notes-field__label">Note /20</span>
                        <input
                          type="number"
                          min={0}
                          max={20}
                          step={0.25}
                          className="academia-field w-full"
                          value={gradeForm.noteSur20}
                          onChange={(e) => setGradeForm((f) => ({ ...f, noteSur20: e.target.value }))}
                        />
                      </label>
                      <label className="formateur-notes-field formateur-notes-field--full">
                        <span className="formateur-notes-field__label">Commentaire</span>
                        <textarea
                          className="academia-field w-full"
                          rows={2}
                          value={gradeForm.commentaireFormateur}
                          onChange={(e) =>
                            setGradeForm((f) => ({ ...f, commentaireFormateur: e.target.value }))
                          }
                        />
                      </label>
                    </div>
                    <div className="mt-4">
                      <button
                        type="button"
                        className="formateur-sessions-btn formateur-sessions-btn--primary"
                        onClick={() =>
                          void runAction(
                            async () => {
                              await gradeFormateurTentative(tentativeDetail.id, {
                                noteSur20: gradeForm.noteSur20 ? Number(gradeForm.noteSur20) : undefined,
                                commentaireFormateur: gradeForm.commentaireFormateur || undefined,
                              });
                              return { message: 'Note enregistrée — relevé mis à jour' };
                            },
                            'Enregistrement impossible',
                          )
                        }
                      >
                        Enregistrer la note
                      </button>
                    </div>
                  </AcademicPanelCard>
                ) : null}
              </>
            ) : (
              <AcademicPanelCard
                icon="quiz"
                title="Sélectionnez une épreuve"
                subtitle="Gérez le lancement live, consultez le suivi en direct et corrigez les copies depuis le panneau de droite."
              >
                <p className="formateur-sessions-empty">
                  Choisissez une carte dans la liste pour afficher les actions disponibles.
                </p>
              </AcademicPanelCard>
            )}
          </div>
        </div>
      </div>
    </FormateurPageShell>
  );
}
